import { useLayoutEffect, useState } from 'react';

// Sub-pixel jitter (zoom, fractional layout) must not count as a change, or the observer and the
// state update would feed each other.
const SAME_WIDTH_EPSILON = 0.5;

// Reads the laid-out width of every direct child of a container, in DOM order.
//
// Its caller uses it on a hidden measurement layer, where each chip renders at the SAME styling as
// the inline strip — so a cap-truncated chip reports its capped width. Why a separate layer at all:
// the alternative is measuring the rendered strip, i.e. deciding from the space LEFT OVER after the
// chips render, which oscillates by construction (show a chip → less free space → hide it → more
// free space → show it). da-19657 design.md → Risks forbids exactly that.
//
// `useLayoutEffect`, not `useEffect`: a setState from a layout effect is flushed before the browser
// paints, so the frame that renders before the first measurement is never actually shown.
const useChildWidths = (signature: string): [(node: HTMLElement | null) => void, number[]] => {
  const [node, setNode] = useState<HTMLElement | null>(null);
  const [widths, setWidths] = useState<number[]>([]);

  useLayoutEffect(() => {
    if (!node) return undefined;

    const read = () => {
      const next = Array.from(node.children).map((el) => el.getBoundingClientRect().width);
      // Bail out when nothing moved: the observer fires on every layout of the layer, and an
      // unconditional setState would re-render forever.
      setWidths((prev) =>
        prev.length === next.length && prev.every((w, i) => Math.abs(w - next[i]) < SAME_WIDTH_EPSILON)
          ? prev
          : next,
      );
    };

    read();
    // One observed element, not one per child: the container is `width: max-content`, so its box is
    // the sum of the children's — any child growing (a web font finishing, an i18n label resolving
    // late) already moves the container.
    //
    // This observer cannot loop, structurally rather than by luck: the layer renders EVERY chip and
    // never reads the visible count, so no state this hook produces can change the size it measures.
    const observer = new ResizeObserver(read);
    observer.observe(node);
    return () => observer.disconnect();
  }, [node, signature]);

  return [setNode, widths];
};

export default useChildWidths;
